import React from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRightOnRectangleIcon,
  CalendarDaysIcon,
  CalendarIcon,
  HomeModernIcon,
  TableCellsIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@material-tailwind/react";
import { usePortalAuth } from "../context/PortalAuthContext";
import {
  ADMIN_SECTIONS,
  isAdminSectionActive,
} from "../constants/adminSections";

function navClass({ isActive }) {
  return [
    "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
    isActive
      ? "bg-brand-lavender/20 text-brand-navy shadow-sm"
      : "text-brand-dark hover:bg-black/[0.04] hover:text-brand-dark",
  ].join(" ");
}

const AUTH_PATHS = ["/login", "/register", "/forgot-password", "/reset-password"];

export default function PortalLayout() {
  const { user, logout, isAuthenticated, authReady } = usePortalAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthRoute = AUTH_PATHS.includes(location.pathname);
  const isAdminRoute = location.pathname.startsWith("/admin");
  const contentMax = isAdminRoute ? "max-w-[90%]" : "max-w-5xl";

  // Auth pages are full-screen with their own layout — no nav/footer
  if (isAuthRoute) {
    return <Outlet />;
  }

  if (!authReady) {
    return (
      <div className="min-h-screen flex flex-col bg-brand-beige text-brand-dark overflow-x-hidden">
        <main className={`flex-1 ${contentMax} w-full mx-auto px-3 sm:px-4 py-16 flex items-center justify-center min-w-0`}>
          <p className="text-brand-gray text-sm">Cargando sesión…</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-brand-beige text-brand-dark overflow-x-hidden">
      <a
        href="#contenido-principal"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-white focus:rounded-lg focus:shadow"
      >
        Ir al contenido
      </a>
      <header className="sticky top-0 z-50 border-b border-brand-brown/20 bg-white/95 backdrop-blur-md shadow-sm">
        <div className={`${contentMax} mx-auto px-3 sm:px-4 py-3 sm:py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between min-w-0`}>
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img
              src="/images/Logo-v2.svg"
              alt="EAP Consulting"
              className="h-10 sm:h-12 w-auto"
            />
            <div className="w-px h-8 bg-brand-lavender/30 hidden sm:block" aria-hidden />
            <img
              src="/images/logo_comp.svg"
              alt=""
              className="h-7 sm:h-8 w-auto hidden sm:block"
            />
            <div className="leading-tight">
              <span className="font-sans text-lg text-brand-dark font-semibold block">
                Agenda de citas
              </span>
              {isAuthenticated && user && (
                <span className="text-xs text-brand-gray font-sans font-normal">
                  {user.firstName
                    ? `Hola, ${user.firstName}`
                    : user.email || ""}
                </span>
              )}
            </div>
          </Link>
          <nav
            className="flex flex-wrap items-center gap-1 sm:gap-0.5 justify-end"
            aria-label="Principal"
          >
            {isAuthenticated && user?.role === "client" && (
              <>
                <NavLink to="/panel" className={navClass}>
                  <HomeModernIcon className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="hidden sm:inline">Panel</span>
                  <span className="sm:hidden">Inicio</span>
                </NavLink>
                <NavLink to="/agendar" className={navClass}>
                  <CalendarDaysIcon className="h-4 w-4 shrink-0" aria-hidden />
                  Agendar
                </NavLink>
                <NavLink to="/mis-citas" className={navClass}>
                  <CalendarIcon className="h-4 w-4 shrink-0" aria-hidden />
                  Mis citas
                </NavLink>
                <NavLink to="/mi-perfil" className={navClass}>
                  <UserCircleIcon className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="hidden sm:inline">Mi perfil</span>
                  <span className="sm:hidden">Perfil</span>
                </NavLink>
              </>
            )}
            {isAuthenticated && user?.role === "admin" && (
              <>
                {ADMIN_SECTIONS.map(({ key, path, label, icon: Icon }) => (
                  <NavLink
                    key={key}
                    to={path}
                    className={({ isActive }) =>
                      navClass({ isActive: isActive || isAdminSectionActive(location.pathname, key) })
                    }
                  >
                    <Icon className="h-4 w-4 shrink-0" aria-hidden />
                    {label}
                  </NavLink>
                ))}
              </>
            )}
            {isAuthenticated && user?.role === "psychologist" && (
              <>
                <NavLink to="/psicologo" className={navClass}>
                  <HomeModernIcon className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="hidden sm:inline">Inicio</span>
                  <span className="sm:hidden">Inicio</span>
                </NavLink>
                <NavLink to="/psicologo/perfil" className={navClass}>
                  <UserCircleIcon className="h-4 w-4 shrink-0" aria-hidden />
                  Perfil
                </NavLink>
                <NavLink to="/psicologo/citas" className={navClass}>
                  <TableCellsIcon className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="hidden sm:inline">Lista</span>
                  <span className="sm:hidden">Lista</span>
                </NavLink>
                <NavLink to="/psicologo/calendario" className={navClass}>
                  <CalendarDaysIcon className="h-4 w-4 shrink-0" aria-hidden />
                  Calendario
                </NavLink>
              </>
            )}
            {!isAuthenticated ? (
              <>
                <Link to="/revistas">
                  <Button size="sm" variant="text" className="text-brand-dark">
                    Conexión Siete
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="sm" variant="text" className="text-brand-dark">
                    Acceso
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="bg-brand-lavender text-white shadow-md shadow-brand-lavender/25">
                    Registro
                  </Button>
                </Link>
              </>
            ) : (
              <Button
                size="sm"
                variant="outlined"
                className="border-brand-brown/40 flex items-center gap-1.5"
                onClick={() => {
                  logout();
                  navigate("/");
                }}
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4" aria-hidden />
                Salir
              </Button>
            )}
            <a
              href="https://eapconsulting.mx"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-brand-gray ml-1 sm:ml-2 px-2 py-2 hover:text-brand-dark whitespace-nowrap"
            >
              Sitio corporativo
            </a>
          </nav>
        </div>
      </header>
      <main
        id="contenido-principal"
        className={`flex-1 ${contentMax} w-full mx-auto px-3 sm:px-4 py-6 sm:py-8 md:py-10 min-w-0`}
        tabIndex={-1}
      >
        <Outlet />
      </main>
      <footer className="border-t border-brand-brown/20 py-6 text-center text-xs text-brand-gray">
        <p>EAP Consulting · Programa de Asistencia al Empleado</p>
        <a
          href="https://eapconsulting.mx/politica-privacidad"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-brand-dark mt-1 inline-block"
        >
          Aviso de privacidad
        </a>
      </footer>
    </div>
  );
}
